import {
  createCsvBlob
} from '../csvEncoding'

import {
  csvEscape,
  downloadBlob,
  ensureStatisticsPdfFonts,
  fileStamp,
  format2,
  formatMonthExportLabel
} from './common'

export const buildCostsExportBlock = (
  stats,
  periodText,
  vehicleText,
  lang,
  t
) => {
  const chartData = (stats?.costsChartData || []).map((item) => ({
    month: item.month,
    monthLabel: formatMonthExportLabel(item.month, lang),
    cost: Number(item.cost || 0)
  }))

  return {
    headers: [
      t('TRIPLOG.STATISTICS.MONTH', {
        defaultValue: 'Mesiac'
      }),
      t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_COST', {
        defaultValue: 'Celkové náklady'
      })
    ],

    rows: chartData.map((item) => [
      item.monthLabel,
      format2(item.cost)
    ]),

    chartData,

    meta: {
      period: periodText,
      vehicle: vehicleText
    }
  }
}

export const exportCostsCSV = (block, t) => {
  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.COSTS_TAB', {
        defaultValue: 'Náklady'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    block?.headers || [],
    ...(block?.rows || [])
  ]
    .map((row) =>
      row.map(csvEscape).join(';')
    )
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `costs_statistics_${fileStamp()}.csv`
  )
}

export const exportCostsXLS = async (block, t) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.COSTS_TAB', {
        defaultValue: 'Náklady'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const headers = block?.headers || []
  const rows = block?.rows || []

  const worksheetData = [
    ...titleRows,
    headers,
    ...rows
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  const columnCount = Math.max(
    1,
    ...worksheetData.map((row) => row.length)
  )

  ws['!cols'] = Array.from(
    {
      length: columnCount
    },
    (_, columnIndex) => ({
      wch: columnIndex === 0 ? 24 : 20
    })
  )

  const mainTitleCell = XLSX.utils.encode_cell({
    r: 0,
    c: 0
  })

  if (ws[mainTitleCell]) {
    ws[mainTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 14,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const subTitleCell = XLSX.utils.encode_cell({
    r: 1,
    c: 0
  })

  if (ws[subTitleCell]) {
    ws[subTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 11,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  ;[3, 4].forEach((rowIndex) => {
    const labelCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 0
    })

    if (ws[labelCell]) {
      ws[labelCell].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }

    const valueCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 1
    })

    if (ws[valueCell]) {
      ws[valueCell].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }
  })

  const headerRowIndex = titleRows.length

  headers.forEach((_, columnIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: columnIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: {
          rgb: 'F0F0F0'
        }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  rows.forEach((row, rowIndex) => {
    row.forEach((_, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: columnIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    t('TRIPLOG.STATISTICS.COSTS_TAB', {
      defaultValue: 'Náklady'
    })
  )

  XLSX.writeFile(
    wb,
    `costs_statistics_${fileStamp()}.xlsx`
  )
}

export const exportCostsPDF = async (block, t) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')

  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const headers = block?.headers || []
  const rows = block?.rows || []
  const chartData = block?.chartData || []

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const pageMarginLeft = 18
  const pageMarginRight = 18

  const usableWidth =
    pageWidth -
    pageMarginLeft -
    pageMarginRight

  const columnWidth =
    usableWidth /
    Math.max(headers.length, 1)

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(0, 0, 0)

  doc.text(
    t('TRIPLOG.STATISTICS.TITLE', {
      defaultValue: 'Štatistiky'
    }),
    pageMarginLeft,
    28
  )

  doc.setFontSize(11)

  doc.text(
    t('TRIPLOG.STATISTICS.COSTS_TAB', {
      defaultValue: 'Náklady'
    }),
    pageMarginLeft,
    44
  )

  doc.setFontSize(9)

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
      defaultValue: 'Zobrazené obdobie'
    })}: ${block?.meta?.period || ''}`,
    pageMarginLeft,
    60
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
      defaultValue: 'Zobrazené vozidlo'
    })}: ${block?.meta?.vehicle || ''}`,
    pageMarginLeft,
    74
  )

  const chartLeft = 68
  const chartRight = pageWidth - 28
  const chartTop = 104
  const chartHeight = 220
  const chartBottom = chartTop + chartHeight
  const chartWidth = chartRight - chartLeft

  const values = chartData.map((item) =>
    Number(item.cost || 0)
  )

  const rawMaxValue = Math.max(
    0,
    ...values
  )

  let yStep

  if (rawMaxValue <= 60) {
    yStep = 20
  } else if (rawMaxValue <= 150) {
    yStep = 30
  } else if (rawMaxValue <= 300) {
    yStep = 60
  } else if (rawMaxValue <= 600) {
    yStep = 100
  } else {
    yStep =
      Math.ceil(
        rawMaxValue / 5 / 100
      ) * 100
  }

  const maxValue = Math.max(
    yStep,
    Math.ceil(rawMaxValue / yStep) * yStep
  )

  const tickCount =
    Math.max(
      1,
      Math.round(maxValue / yStep)
    )

  const getY = (value) =>
    chartBottom -
    (
      Number(value || 0) /
      maxValue
    ) *
      chartHeight

  const getX = (index) => {
    if (chartData.length <= 1) {
      return chartLeft + chartWidth / 2
    }

    return (
      chartLeft +
      (
        index /
        (chartData.length - 1)
      ) *
        chartWidth
    )
  }

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(8)

  for (
    let tickIndex = 0;
    tickIndex <= tickCount;
    tickIndex += 1
  ) {
    const value =
      tickIndex * yStep

    const y =
      getY(value)

    doc.setDrawColor(225, 225, 225)
    doc.setLineWidth(0.5)
    doc.setLineDashPattern([], 0)

    doc.line(
      chartLeft,
      y,
      chartRight,
      y
    )

    doc.setTextColor(90, 90, 90)

    doc.text(
      String(value),
      chartLeft - 12,
      y + 3,
      {
        align: 'right'
      }
    )
  }

  doc.setDrawColor(185, 185, 185)
  doc.setLineWidth(0.6)
  doc.setLineDashPattern([], 0)

  doc.line(
    chartLeft,
    chartBottom,
    chartRight,
    chartBottom
  )

  const pointCoordinates =
    chartData.map((item, index) => ({
      x: getX(index),
      y: getY(item.cost),
      value: Number(item.cost || 0),
      monthLabel:
        item.monthLabel ||
        item.month ||
        ''
    }))

  pointCoordinates.forEach((point) => {
    doc.setDrawColor(180, 180, 180)
    doc.setLineWidth(0.7)
    doc.setLineDashPattern([4, 4], 0)

    doc.line(
      point.x,
      point.y,
      point.x,
      chartBottom
    )
  })

  if (pointCoordinates.length >= 2) {
    doc.setDrawColor(16, 124, 107)
    doc.setLineWidth(2.5)
    doc.setLineDashPattern([], 0)

    for (
      let index = 0;
      index < pointCoordinates.length - 1;
      index += 1
    ) {
      const current =
        pointCoordinates[index]

      const next =
        pointCoordinates[index + 1]

      doc.line(
        current.x,
        current.y,
        next.x,
        next.y
      )
    }
  }

  pointCoordinates.forEach((point) => {
    doc.setFillColor(16, 124, 107)
    doc.setDrawColor(16, 124, 107)

    doc.circle(
      point.x,
      point.y,
      4,
      'F'
    )

    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(30, 30, 30)

    doc.text(
      `${format2(point.value)} €`,
      point.x,
      Math.max(
        chartTop + 9,
        point.y - 9
      ),
      {
        align: 'center'
      }
    )

    doc.setFontSize(8)
    doc.setTextColor(70, 70, 70)

    doc.text(
      String(point.monthLabel),
      point.x,
      chartBottom + 16,
      {
        align: 'center'
      }
    )
  })

  if (values.length >= 2) {
    const count = values.length

    const sumX =
      values.reduce(
        (sum, _value, index) =>
          sum + index,
        0
      )

    const sumY =
      values.reduce(
        (sum, value) =>
          sum + value,
        0
      )

    const sumXY =
      values.reduce(
        (sum, value, index) =>
          sum + index * value,
        0
      )

    const sumXX =
      values.reduce(
        (sum, _value, index) =>
          sum + index * index,
        0
      )

    const denominator =
      count * sumXX -
      sumX * sumX

    const slope =
      denominator !== 0
        ? (
            count * sumXY -
            sumX * sumY
          ) / denominator
        : 0

    const intercept =
      (
        sumY -
        slope * sumX
      ) / count

    const trendStartValue =
      Math.max(
        0,
        intercept
      )

    const trendEndValue =
      Math.max(
        0,
        intercept +
        slope * (count - 1)
      )

    doc.setDrawColor(92, 210, 194)
    doc.setLineWidth(1.4)
    doc.setLineDashPattern([6, 5], 0)

    doc.line(
      getX(0),
      getY(trendStartValue),
      getX(count - 1),
      getY(trendEndValue)
    )

    doc.setLineDashPattern([], 0)
  }

  let tableStartY =
    chartBottom + 34

  if (tableStartY > pageHeight - 90) {
    doc.addPage()

    tableStartY = 28
  }

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: tableStartY,
    theme: 'grid',

    margin: {
      top: 18,
      right: pageMarginRight,
      bottom: 18,
      left: pageMarginLeft
    },

    tableWidth: usableWidth,

    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 9,
      cellPadding: 4,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 14
    },

    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 9,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },

    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },

    columnStyles: headers.reduce(
      (acc, _header, index) => {
        acc[index] = {
          cellWidth: columnWidth
        }

        return acc
      },
      {}
    ),

    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `costs_statistics_${fileStamp()}.pdf`
  )
}